import {
  autoUpdate,
  FloatingOverlay,
  FloatingPortal,
  flip,
  offset,
  shift,
  type UseFloatingOptions,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { css, cx } from '@linaria/core'
import { type FC, useRef, useState } from 'react'
import { Icon } from '#components/Icon'
import CloseIcon from '#icons/close-24.svg'
import { style } from '#styles/index'
import type { DeepPartial } from '#types/DeepPartial'
import type { GalleryProperties } from './Gallery'
import { Indicator } from './Indicator'
import { MediaList } from './MediaList'

const fullscreenGalleryClassName = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: calc(100vw * var(--inverse-zoom));
  max-width: calc(100vw * var(--inverse-zoom));
  height: calc(100vh * var(--inverse-zoom));

  background: rgba(0, 0, 0, 0.8);


  > .gallery {
    display: flex;
    max-height: 80vh;

    > .indicator {
      position: fixed;
      bottom: 0;
      width: 100vw;
      padding: 0 20vw;
      > .indicator {
        cursor: pointer;
        padding-top: 24px;
        padding-bottom: 24px;
      }
    }

    > .icon {
      cursor: pointer;
      position: fixed;
      top: 12px;
      right: 12px;
      color: ${style.themeColors.textImportant};
    }
  }

  // ensure the gallery sits centered in the middle
  > .indicator, .spacer {
    flex: 1;
    width: 80vw;
  }

  > .indicator {
    padding: 0 20vw 24px 20vw;
  }
`
export const FullscreenGallery: FC<
  DeepPartial<GalleryProperties> & {
    items: GalleryProperties['items']
    open: boolean
    onOpenChange?: UseFloatingOptions['onOpenChange']
    overlayClassName?: string
    galleryClassName?: string
  }
> = ({ items, open, onOpenChange, overlayClassName, galleryClassName }) => {
  const { refs, context } = useFloating({
    open,
    middleware: [offset(100), flip(), shift()],
    whileElementsMounted: autoUpdate,
    onOpenChange,
  })

  const dismiss = useDismiss(context, { outsidePressEvent: 'click', enabled: true })
  const role = useRole(context, { role: 'alertdialog' })
  const { getFloatingProps } = useInteractions([dismiss, role])

  const listRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLImageElement | HTMLVideoElement | null)[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)

  if (!open) {
    return undefined
  }

  return (
    <FloatingPortal>
      <FloatingOverlay className={cx(fullscreenGalleryClassName, overlayClassName)} lockScroll>
        {/** biome-ignore lint/complexity/noUselessFragments: it's not useless, FloatingFocusManager only accepts React.JSX.Element */}
        <>
          <div ref={refs.setFloating} {...getFloatingProps()} className={cx('gallery', galleryClassName)}>
            <Icon className="icon" iconUrl={CloseIcon} size="xl" onClick={() => onOpenChange?.(false)} />
            <MediaList
              items={items}
              currentIndex={currentIndex}
              ref={listRef}
              onItemRef={(i, instance) => {
                itemRefs.current[i] = instance
              }}
              onItemFocused={(i, _item) => setCurrentIndex(i)}
            />

            <Indicator
              itemCount={items.length}
              currentIndex={currentIndex}
              onIndicatorClicked={(evt, i) => {
                evt.stopPropagation()
                setCurrentIndex(i)
              }}
              withArrows={false}
            />
          </div>
        </>
      </FloatingOverlay>
    </FloatingPortal>
  )
}
